/**
 * Fetches the plan catalog from the backend, falling back to the bundled copy.
 *
 * Uses bare axios rather than useApi()/apiClient on purpose: this runs on the
 * PUBLIC pricing page. useApi() awaits an Auth0 token unguarded and rejects for
 * logged-out visitors, and apiClient's response interceptor redirects to /login
 * on 401. Neither is acceptable for a marketing page.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { PLAN_CATALOG_FALLBACK, PlanCatalog } from "../config/plans";

// Module-level cache so navigating landing -> /pricing doesn't refetch.
let cached: PlanCatalog | null = null;

export function usePlanCatalog(): { catalog: PlanCatalog; stale: boolean } {
  // Render synchronously with the fallback — never a spinner where a price should be.
  const [catalog, setCatalog] = useState<PlanCatalog>(cached || PLAN_CATALOG_FALLBACK);
  const [stale, setStale] = useState(!cached);

  useEffect(() => {
    if (cached) return;
    let cancelled = false;

    axios
      .get("/api/plans", { baseURL: process.env.REACT_APP_API_URL || "" })
      .then((res) => {
        if (cancelled) return;
        const data = res.data;
        if (data && Array.isArray(data.plans) && data.plans.length > 0) {
          cached = { currency: data.currency, features: data.features, plans: data.plans };
          setCatalog(cached);
          setStale(false);
        }
      })
      .catch(() => {
        // Keep the fallback; pricing must always render.
        if (!cancelled) setStale(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { catalog, stale };
}
